import { ChangeEvent, useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { ThemingKbaFormValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { AXNUIContentKBA } from "@iddataweb/idw-common-ui/types/axnui-content/axnui-content-kba";

type ThemingKbaFormTabProps = {
    isDisabled: boolean;
    validationErrors: ThemingKbaFormValidationErrors;
    axnuiContent: AXNUIContent;
    inheritedAXNUIContent: AXNUIContent;
    pageLevel: PageLevelTypes;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
};

export const ThemingKbaFormTabContainer = (
    props: ThemingKbaFormTabProps
) => {
    const {
        isDisabled,
        validationErrors,
        axnuiContent,
        inheritedAXNUIContent,
        pageLevel,
        generatePreviewComponent,
        updateAXNUIContentPropertyValue,
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const resource =
        resources.pages.verificationServiceDetails.tabs.theming.subTabs.kbaForm;

    const updateAXNUIContentKbaFormFieldValue = (
        key: keyof AXNUIContentKBA,
        value: string
    ) => {
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            kba: {
                ...axnuiContent.kba,
                [key]: value,
            },
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const handleHelloMessageChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateAXNUIContentKbaFormFieldValue(
            "helloMessage",
            newValue
        );
    };

    const handleRetryMessageChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateAXNUIContentKbaFormFieldValue(
            "retryMessage",
            newValue
        );
    };

    const handleResetHelloMessage = () => {
        updateAXNUIContentKbaFormFieldValue(
            "helloMessage",
            inheritedAXNUIContent.kba.helloMessage
        );
    };

    const handleResetRetryMessage = () => {
        updateAXNUIContentKbaFormFieldValue(
            "retryMessage",
            inheritedAXNUIContent.kba.retryMessage
        );
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.KBA_FORM);
    }, [generatePreviewComponent]);

    return (
        <div>
            <ContentPageHeader
                title={resource.title}
            />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer
                    description={resource.description}
                />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedAXNUIContent.kba.helloMessage}
                            currentThemingPropertyValue={axnuiContent.kba.helloMessage}
                            pageLevel={pageLevel}
                        >
                            <InputFormGroup
                                controlId={"kba-form-hello-message-input"}
                                automationId={"kba-form-hello-message-input"}
                                label={resource.inputs.helloMessage.label}
                                ariaLabel={resource.inputs.helloMessage.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleHelloMessageChange}
                                value={axnuiContent.kba.helloMessage || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={resource.inputs.helloMessage.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetHelloMessage}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.helloMessageError}

                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedAXNUIContent.kba.retryMessage}
                            currentThemingPropertyValue={axnuiContent.kba.retryMessage}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={"kba-form-retry-message-input"}
                                automationId={"kba-form-retry-message-input"}
                                label={resource.inputs.retryMessage.label}
                                ariaLabel={resource.inputs.retryMessage.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleRetryMessageChange}
                                value={axnuiContent.kba.retryMessage || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={resource.inputs.retryMessage.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetRetryMessage}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.retryMessageError}
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};
